import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IBankCreationDTO } from '@common-frontend-submodule/interfaces/bank/ibank-creation.interface';
import { IBankUpdateDTO } from '@common-frontend-submodule/interfaces/bank/ibank-update.interface';
import { IGetBanksRequestDTO } from '@common-frontend-submodule/interfaces/bank/iget-banks-request.interface';
import { IBankDTO } from '@common-frontend-submodule/interfaces/bank/ibank.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { BankRoutes } from '@common-frontend-submodule/enums/routes/bank-routes.enum';
// import { IBankShortDTO } from '@common-frontend-submodule/interfaces/bank/ibank-short.interface';

const apiClient = ApiClient.getInstance()

interface BankContextProps {
  getById: (bankId: string) => Promise<IApiResponseGenericDTO<IBankDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<IBankDTO[]>>;
  filter: (model: IGetBanksRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IBankDTO>>>;
  create: (model: IBankCreationDTO) => Promise<IApiResponseGenericDTO<IBankDTO>>;
  update: (bankId: string, model: IBankUpdateDTO) => Promise<IApiResponseGenericDTO<IBankDTO>>;
  delete: (bankId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const BankContext = createContext<BankContextProps | undefined>(undefined);

export const BankProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (bankId: string): Promise<IApiResponseGenericDTO<IBankDTO>> => {
    return await apiClient.getRequest<IBankDTO>(`${BankRoutes.BasePrefix}/${bankId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IBankDTO[]>> => {
    return await apiClient.getRequest<IBankDTO[]>(`${BankRoutes.BasePrefix}/${BankRoutes.All}`);
  };

  const filter = async (model: IGetBanksRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IBankDTO>>> => {
    return await apiClient.postRequest<IGetBanksRequestDTO, IPaginatedResultDTO<IBankDTO>>(
      `${BankRoutes.BasePrefix}/${BankRoutes.List}`,
      model
    );
  };

  const create = async (model: IBankCreationDTO): Promise<IApiResponseGenericDTO<IBankDTO>> => {
    return await apiClient.postRequest<IBankCreationDTO, IBankDTO>(BankRoutes.BasePrefix, model);
  };

  const update = async (bankId: string, model: IBankUpdateDTO): Promise<IApiResponseGenericDTO<IBankDTO>> => {
    return await apiClient.putRequest<IBankUpdateDTO, IBankDTO>(`${BankRoutes.BasePrefix}/${bankId}`, model);
  };

  const deleteBank = async (bankId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${BankRoutes.BasePrefix}/${bankId}`);
  };

  return (
    <BankContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteBank }}>
      {children}
    </BankContext.Provider>
  );
};

export const useBankStore = () => {
  const context = useContext(BankContext);
  if (!context) {
    throw new Error('useBankStore must be used within a BankProvider');
  }
  return context;
};
